/**
 * All UI copy lives here so the portal can be localized later without
 * hunting strings through components. English only for now.
 */
export const t = {
    common: {
        brand: 'Routellers',
        portal: 'Author Portal',
        save: 'Save changes',
        cancel: 'Cancel',
        continue: 'Continue',
        back: 'Back',
        delete: 'Delete',
        loading: 'Loading…',
        somethingWentWrong: 'Something went wrong. Please try again.',
        saved: 'Saved',
    },
    auth: {
        loginTitle: 'Welcome back',
        loginSubtitle: 'Log in to your author account',
        email: 'Email',
        password: 'Password',
        login: 'Log in',
        forgotPassword: 'Forgot password?',
        noAccount: 'New quest author?',
        createAccount: 'Create an account',
        haveAccount: 'Already have an account?',

        registerTitle: 'Join as a quest author',
        registerSubtitle:
            'Create city quests and audio-guided routes — earn a commission from every sale once they go live',
        fullName: 'Full name',
        passwordHint: 'At least 8 characters',
        createAccountButton: 'Create account',
        terms: 'By creating an account you agree to be contacted by our team about the author program.',

        verifyTitle: 'Check your inbox',
        verifySubtitle: (email: string) =>
            `We sent a 6-digit code to ${email}. Enter it below to activate your account.`,
        verifyButton: 'Confirm email',
        resend: 'Resend code',
        resendDone: 'A new code is on its way',
        codeInvalid: 'Enter the 6-digit code',

        forgotTitle: 'Reset your password',
        forgotSubtitle: 'Enter your email and we will send you a reset code.',
        sendCode: 'Send code',
        codeSent: 'If this email is registered, a code is on its way.',
        newPassword: 'New password',
        resetButton: 'Set new password',
        backToLogin: 'Back to log in',
    },
    nav: {
        dashboard: 'Dashboard',
        stories: 'My quests',
        routes: 'My routes',
        earnings: 'Earnings',
        settings: 'Settings',
        logout: 'Log out',
    },
    dashboard: {
        welcome: (name: string) => `Hello, ${name.split(' ')[0]}!`,
        authorSubtitle: 'Here is how your quests and routes are doing.',
        commission: 'Your commission',
        commissionPendingTitle: 'Commission under review',
        commissionPendingText:
            'Our team is reviewing your account. Your personal commission rate will appear here once it is agreed — usually within a couple of days.',
        gamesPlayed: 'Total sales',
        totalEarned: 'Total earned',
        monthlyChart: 'Sales per month',
        earningsChart: 'Earnings per month',
        noSalesYet: 'No sales yet',
        noSalesAuthorText:
            'Once one of your quests or routes is live and sold, numbers will show up here.',
    },
    stories: {
        title: 'My quests',
        subtitle:
            'Write a walking quest — we review it and publish the best ones to the player app.',
        newStory: 'New quest',
        empty: 'No quests yet',
        emptyText:
            'Start with your first quest: pick a city you know well and design a walk players will remember.',
        statuses: {
            draft: 'Draft',
            need_review: 'In review',
            rejected: 'Needs changes',
            ready: 'Live',
            paused: 'Paused',
        },
        steps: (n: number) => `${n} ${n === 1 ? 'stop' : 'stops'}`,
        submit: 'Submit for review',
        submitted:
            'Submitted for review. We usually reply within a few days — check email for updates.',
        deleteConfirm: 'Delete this quest? This cannot be undone.',
        rejectionTitle: 'Feedback from our team',
        titleRequired: 'Enter a quest title',
        needStops: 'Add at least one stop with an attraction before saving.',
        needRiddles:
            'Tip: each stop should have a riddle and answer so players have something to solve.',

        guideTitle: 'How to write a great quest',
        guideIntro:
            'Think of a 60–90 minute walk with a clear story. Players move stop-to-stop, read a short scene, solve a riddle, then continue.',
        guideBullets: [
            '6–12 stops, total route about 2–4 km',
            'Every stop needs an approved attraction from the catalog',
            'Player flow at each stop: on the way → arrive → story → riddle → after solve',
            'Riddles should be solvable on site (look, count, read a plaque) — not trivia from home',
            'Write in one language. We handle translations after review',
            'Submit only when attractions are approved and the route feels walkable',
        ],

        editorTitleNew: 'New quest',
        editorTitleEdit: 'Edit quest',
        storyTitle: 'Quest title',
        storyTitlePlaceholder: 'e.g. The Stolen Hour of the Old Town',
        storyTitleHint: 'Shown in the catalog and on the start screen.',
        description: 'Synopsis',
        descriptionPlaceholder:
            'What is the quest about? Who are the players and what must they accomplish?',
        descriptionHint: 'One short paragraph for reviewers and the catalog.',
        citySelect: 'City',
        citySelectPlaceholder: 'Search for a city…',
        cityRequired: 'Choose a city before adding stops',
        cityLockedNotice:
            'City is locked because stops already use attractions. Clear attractions from all stops to change city.',
        language: 'Writing language',
        languageHint:
            'Primary language of your texts. Locked after you add stop text — change only if you rewrite.',
        languageLockedNotice:
            'Language is locked because this quest already has text. Clear stop texts to change it.',
        stepsTitle: 'Stops',
        stepsSubtitle:
            'Each stop is one place on the walk: attraction, story, and a riddle. Aim for 6–12 stops.',
        addStep: 'Add stop',
        stepTitle: 'Stop name',
        stepTitlePlaceholder: 'e.g. Old Town Clock',
        stepTitleHint: 'Internal label; players mainly see the attraction name.',
        pickAttraction: 'Choose attraction',
        changeAttraction: 'Change attraction',
        noAttractionSelected: 'No attraction selected',
        createAttraction: 'Create new attraction',
        attractionSearch: 'Search attractions',
        attractionSearchPlaceholder: 'Name, district, known for…',
        attractionCategory: 'Category',
        attractionFame: 'Fame',
        attractionRouteRole: 'Route role',
        attractionIndoorOutdoor: 'Indoor / outdoor',
        attractionDistrict: 'District',
        attractionDistrictPlaceholder: 'e.g. Marais',
        allCategories: 'All categories',
        allFame: 'All fame levels',
        attractionNone: 'No attractions match these filters',
        attractionNeedReview: 'Needs review',
        attractionRejected: 'Rejected',
        attractionCreateTitle: 'New attraction',
        attractionTitle: 'Title',
        attractionTitlePlaceholder: 'Official or common name',
        attractionDescription: 'Description',
        attractionDescriptionPlaceholder: 'Short description of the place',
        attractionKnownFor: 'Known for (EN)',
        attractionKnownForRu: 'Known for (RU)',
        attractionKnownForPlaceholder: 'What makes this place special?',
        attractionAddress: 'Address',
        attractionAddressPlaceholder: 'Street address if known',
        attractionLat: 'Latitude',
        attractionLng: 'Longitude',
        attractionDuplicatesWarning:
            'Similar attractions nearby — check you are not creating a duplicate:',
        attractionSaved: 'Attraction created',
        rejectedBlockSubmit:
            'Every attraction must be approved before submit. Replace rejected ones or wait for review.',
        unapprovedBlockSubmit:
            'Every attraction must be approved before submit. Replace ones still in review or wait for approval.',
        routePreview: 'Route preview',
        walkingTime: (mins: number) => `Estimated walking time: ~${mins} min`,
        neighborFarYellow: (from: number, to: number, km: string) =>
            `Stops ${from}→${to} are ${km} km apart — review this leg (target 0.2–0.5 km)`,
        neighborFarRed: (from: number, to: number, km: string) =>
            `Stops ${from}→${to} are ${km} km apart — over 1 km needs a written reason`,
        sequenceWarning: 'Stop order looks suboptimal — consider reordering for a shorter walk',
        softStepsWarning: (n: number) => `${n} stops — aim for 8–15 narrated stories`,
        softDistanceWarning: (km: string) => `Total route ~${km} km — aim for 2–4 km`,
        routeSummary: (steps: number, km: string) => `${steps} stops · ~${km} km total`,
        stepNarrative: 'Story at the stop',
        stepNarrativePlaceholder: 'What players read when they arrive…',
        stepNarrativeHint: 'Shown after “I’m here”. Short scene that leads into the riddle.',
        stepStoryAfter: 'After solve (optional)',
        stepStoryAfterPlaceholder: 'What happens right after they solve the riddle…',
        stepStoryAfterHint: 'Played after a correct answer, before the next walk.',
        stepArrival: 'Arrival tip (optional)',
        stepArrivalPlaceholder: 'How to find the exact spot when they get close…',
        stepArrivalHint: 'Shown with the “I’m here” button.',
        stepFromPrevious: 'On the way (optional)',
        stepFromPreviousPlaceholder: 'Short walk cue from the previous stop…',
        stepFromPreviousHint: 'Shown while walking to this stop — directions or atmosphere.',
        stepWalkTask: 'Walk micro-task (optional)',
        stepWalkTaskPlaceholder: 'Something to notice or do while walking…',
        stepWalkTaskHint: 'Keeps the walk playful between stops.',
        stepRiddle: 'Riddle',
        stepRiddlePlaceholder: 'The task players must solve at this stop…',
        stepRiddleHint: 'Must be solvable on site. Avoid pure trivia.',
        stepAnswer: 'Answer',
        stepAnswerPlaceholder: 'Expected answer',
        stepAnswerHint: 'We accept close spellings; keep one clear canonical answer.',
        stepHint: 'Hint (optional)',
        stepHintPlaceholder: 'A nudge if the players are stuck',
        stepHintHint: 'Costs score points when used in the player.',
        removeStep: 'Remove stop',
        saveDraft: 'Save draft',
        readOnlyNotice:
            'This quest is in review or already live, so it is read-only. Contact us if you need changes.',
    },
    earnings: {
        title: 'Earnings',
        subtitle: 'Commission is calculated from the gross value of every attributed sale.',
        month: 'Month',
        games: 'Sales',
        sales: 'Gross sales',
        commission: 'Your commission',
        perQuest: 'By quest',
        quest: 'Quest',
        noData: 'Nothing here yet — earnings appear after the first sale.',
        payoutNote:
            'Payouts are being set up. Your earnings are safely tracked and will be paid out once payouts launch.',
    },
    routes: {
        title: 'My routes',
        subtitle:
            'Build an audio-guided walking route — we review it and publish the best ones to the player app.',
        newRoute: 'New route',
        empty: 'No routes yet',
        emptyText:
            'Start with your first ready route: pick a city you know well and build a walk with narration at each stop.',
        untitled: 'Untitled route',
        untitledStop: 'Untitled stop',
        statuses: {
            draft: 'Draft',
            need_review: 'In review',
            rejected: 'Needs changes',
            published: 'Live',
        },
        steps: (n: number) => `${n} ${n === 1 ? 'stop' : 'stops'}`,
        submit: 'Submit for review',
        submitted:
            'Submitted for review. We usually reply within a few days — check email for updates.',
        deleteConfirm: 'Delete this route? This cannot be undone.',
        rejectionTitle: 'Feedback from our team',
        titleRequired: 'Enter a route title',
        needStops: 'Add at least 2 stops with attractions before saving.',
        needGuideTalk:
            'Each thematic stop needs one complete route-specific story and matching audio.',
        guideTitle: 'How to write a great route',
        guideIntro:
            'Build a thematic audio walk: one real place, one dominant story, one recording. Every stop advances the theme but still makes sense on its own.',
        guideBullets: [
            'Aim for 8–15 narrated stops and a walkable geographic shape',
            'Every stop needs an approved attraction from the catalog',
            'Give first-time visitors footing, then tell the strongest thematic slice',
            'Ordinary talk: 2.5–4 min; flagship: 4–5.5 min',
            'No transition narration or filler stops; navigation belongs to the map',
            'Target 0.2–0.5 km per leg; review over 0.7 km and explain over 1 km',
            'Write in one language. We handle translations and audio after review',
            'Submit only when attractions are approved and the route feels walkable',
        ],
        editorTitleNew: 'New route',
        editorTitleEdit: 'Edit route',
        routeTitle: 'Route title',
        routeTitlePlaceholder: 'e.g. Lakeside Secrets of Como',
        routeTitleHint: 'Shown in the catalog and on the start screen.',
        teaser: 'Teaser',
        teaserPlaceholder: 'One short line that sells the walk…',
        teaserHint: 'Appears under the title in lists.',
        description: 'Description',
        descriptionPlaceholder: 'What will players see and hear on this walk?',
        descriptionHint: 'One short paragraph for reviewers and the catalog.',
        citySelect: 'City',
        citySelectPlaceholder: 'Search for a city…',
        cityRequired: 'Choose a city before adding stops',
        cityLockedNotice:
            'City is locked because stops already use attractions. Clear attractions from all stops to change city.',
        language: 'Writing language',
        languageHint: 'Primary language of your texts. We translate after review.',
        stepsTitle: 'Stops',
        stepsSubtitle:
            'Each stop is one real place with one dominant thematic story. Aim for 8–15.',
        addStep: 'Add stop',
        stepTitle: 'Stop name',
        stepTitlePlaceholder: 'e.g. Villa Olmo lakeside',
        pickAttraction: 'Choose attraction',
        changeAttraction: 'Change attraction',
        noAttractionSelected: 'No attraction selected',
        attractionNeedReview: 'Needs review',
        stepHook: 'Hook (optional)',
        stepHookPlaceholder: 'A short lead-in as players arrive…',
        stepHookHint: 'Optional one-liner before the main narration.',
        stepGuideTalk: 'Guide talk',
        stepGuideTalkPlaceholder: 'What the audio guide says at this stop…',
        stepGuideTalkHint:
            'Complete thematic narration: usually 2.5–4 min, or 4–5.5 min for a flagship.',
        removeStep: 'Remove stop',
        saveDraft: 'Save draft',
        readOnlyNotice:
            'This route is in review or already live, so it is read-only. Contact us if you need changes.',
        routePreview: 'Route preview',
        unapprovedBlockSubmit:
            'Every attraction must be approved before submit. Replace ones still in review or wait for approval.',
        routeSummary: (steps: number, km: string) => `${steps} stops · ~${km} km total`,
    },
    settings: {
        title: 'Settings',
        profile: 'Profile',
        accountType: 'Account type',
        author: 'Quest author',
        email: 'Email',
        emailNote: 'Contact support to change your email.',
        photo: 'Photo',
        photoHint: 'Shown next to your name on quests and routes. A square image works best.',
        photoInvalid: 'Please choose an image file.',
        changePhoto: 'Change photo',
        removePhoto: 'Remove',
        openSettings: 'Open settings',
    },
};
